import {GameStateEnums} from '../../../../../shared/GameEnums'
import {PIXI} from '@colonist/pixi'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {UIGameMechanic} from '../UIGameEventController'
import {UIGameManager} from '../UIGameManager'
import {UICityHoverActionController} from '../ui_game_hover_actions/UICityHoverActionController'
import {UIRoadHoverActionController} from '../ui_game_hover_actions/UIRoadHoverActionController'
import {UISettlementHoverActionController} from '../ui_game_hover_actions/UISettlementHoverActionController'
import {UIGameActionButtonBuyDevelopmentCard} from './UIGameActionButtonBuyDevelopmentCard'
import {UIGameActionButtonView} from './UIGameActionButtonView'
import {UIGameActionButtonViewCityWall} from './UIGameActionButtonViewCityWall'
import {UIGameActionButtonViewShip} from './UIGameActionButtonViewShip'
import {UIGameActionButtonViewShipMove} from './UIGameActionButtonViewShipMove'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {MechanicCityState, MechanicCityWallState, MechanicDevelopmentCardsState, MechanicPirateState, MechanicRoadState, MechanicSettlementState, MechanicShipState} from '@colonist/game-states'

export enum UIGameActionButtonPositions {
    Trade,
    DevCard,
    CityImprovementMultiButtonMobile,
    CityWall,
    Road,
    Settlement,
    City,
    EndTurn,
    BuildShip,
    MoveShip,
    KnightMultiButton,
    CityImprovementMultiButtonWeb
}
export abstract class UIGameActionsController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected readonly buttonViews: UIGameActionButtonView[]

    readonly container: SectionBgView
    private numberOfColumns: number

    constructor(uiGameManager: UIGameManager, settlementState: MechanicSettlementState | undefined, cityState: MechanicCityState | undefined, roadState: MechanicRoadState | undefined) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.buttonViews = []

        this.container = this.getContainer()
        this.numberOfColumns = 1

        if(this.uiGameManager.gameController.isPlayerSpectator) return
        if(this.uiGameManager.gameController.isReplay) return

        this.addDefaultButtons(settlementState, cityState, roadState)
        this.initializeHoverActions(settlementState, cityState, roadState)
    }

    protected abstract getContainer()
    protected abstract addDefaultButtons(
        settlementState: MechanicSettlementState | undefined,
        cityState: MechanicCityState | undefined,
        roadState: MechanicRoadState | undefined)

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.ShowTradeUI: this.hideTopRow(); break
            case UIGameEventEnums.CloseTradeUI: this.show(); break
            case UIGameEventEnums.PopupActive: this.onPopupActive(data); break
            case UIGameEventEnums.GameEnd: this.disable(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            default: break
        }
    }

    private onPopupActive(popupView: SectionBgView | undefined) {
        if(!UIUtilsEjsVariables.isUserOnMobileApp) return
        if(popupView == undefined) this.show()
        else this.hideTopRow()
    }

    addButton(newButton: UIGameActionButtonView) {
        this.buttonViews.push(newButton)
    }

    activateBuyDevelopmentCardButton(devCardsState: MechanicDevelopmentCardsState) {
        if(this.uiGameManager.gameController.isPlayerSpectator) return
        if(this.uiGameManager.gameController.isReplay) return

        this.buttonViews.push(new UIGameActionButtonBuyDevelopmentCard(this.uiGameManager, devCardsState, this.container))
    }

    activateShipButtons(shipState: MechanicShipState, mechanicPirateState: MechanicPirateState | undefined) {
        if(this.uiGameManager.gameController.isPlayerSpectator) return
        if(this.uiGameManager.gameController.isReplay) return

        this.buttonViews.push(new UIGameActionButtonViewShip(this.uiGameManager, shipState, mechanicPirateState, this.container))
        this.buttonViews.push(new UIGameActionButtonViewShipMove(this.uiGameManager, shipState, mechanicPirateState, this.container))
    }

    activateCityWallButton(cityWallState: MechanicCityWallState) {
        if(this.uiGameManager.gameController.isPlayerSpectator) return
        if(this.uiGameManager.gameController.isReplay) return

        this.buttonViews.push(new UIGameActionButtonViewCityWall(this.uiGameManager, cityWallState, this.container))
    }

    buildButtonController() {
        this.buttonViews.sort((a, b) => a.preferredPosition > b.preferredPosition ? 1 : -1)
        this.resizeContainerForButtons()
        const maxNumberOfColumns = 6
        const totalNumberOfButtonsOnSecondRow = this.buttonViews.length - maxNumberOfColumns
        const maxNumberOfButtonsBeforeButtonsOverlapWithStateController = 2
        const secondRowButtonShift = totalNumberOfButtonsOnSecondRow > maxNumberOfButtonsBeforeButtonsOverlapWithStateController ? totalNumberOfButtonsOnSecondRow - maxNumberOfButtonsBeforeButtonsOverlapWithStateController : 0
        for(const [index, button] of this.buttonViews.entries()) {
            const row = Math.floor(index / this.numberOfColumns)
            const unshiftedColumn = index % this.numberOfColumns
            const secondRowIndex = 1
            const column = row == secondRowIndex ? unshiftedColumn - secondRowButtonShift : unshiftedColumn
            this.repositionButton(button, row, column)
        }
    }

    protected abstract onCanvasResize()

    private initializeHoverActions(settlementState: MechanicSettlementState | undefined, cityState: MechanicCityState | undefined, roadState: MechanicRoadState | undefined): void {
        if(roadState) new UIRoadHoverActionController(this.uiGameManager, roadState)
        if(settlementState) new UISettlementHoverActionController(this.uiGameManager, settlementState)
        if(cityState) new UICityHoverActionController(this.uiGameManager, cityState)
    }

    private resizeContainerForButtons() {
        const numberOfButtons = this.buttonViews.length
        const maxNumberOfColumns = 6
        this.numberOfColumns = Math.min(numberOfButtons, maxNumberOfColumns)
        const canvas = this.uiGameManager.canvasController
        this.container.x = canvas.appW - canvas.chatWidth - this.numberOfColumns * canvas.itemSize
    }

    protected abstract repositionButton(button: UIGameActionButtonView, row: number, column: number)

    private hideTopRow() {
        for(const actionButtonView of this.buttonViews) {
            const isTopRowButton = actionButtonView.y < 0

            if(actionButtonView.parent == undefined) continue
            if(!isTopRowButton) continue

            actionButtonView.parent.removeChild(actionButtonView)
        }
    }

    private show() {
        for(const actionButtonView of this.buttonViews) {
            if(actionButtonView.parent != undefined) continue
            this.container.addChild(actionButtonView)
        }
    }

    private disable() {
        for(const actionButtonView of this.buttonViews) {
            actionButtonView.interactive = false
        }
    }

    // Jan 11 2020. Jeff: This function was only written to hack in a solution for tutorial modes during refactoring
    // please don't use this function regularly
    getButtonWithTexture(texture: PIXI.Texture): UIGameActionButtonView | undefined {
        for(const buttonView of this.buttonViews) {
            if(buttonView.isSameTexture(texture)) return buttonView
        }
        return undefined
    }
}
